import {
  AccountView,
  EmployeeView,
  PayrollEvidenceLineItemView,
  PayrollRecordView,
  PayrollView,
} from '@seapoint/client/src/types/entity-types';
import { Currency, FinancialServiceProviderAdapters, PayrollStatus } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { ComponentProps, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { EmployeeFormValues } from '../components/add-employees-step/employee-drawer/employee-form';
import { PayrollFlowModal } from '../components/payroll-flow-modal';
import { PayrollFormData } from '../components/upload-document-step/components/net-pay-form';
import { usePayrollFlow } from '../use-payroll-flow';
import { createAddressObject, createDestinationObject } from '../utils/employee-drawer-helpers';
import { useEmployee } from './use-employee';
import { usePayroll } from './use-payroll';
import { usePayrollRecord } from './use-payroll-record';

type Step = 'DOCUMENT_PROCESSING' | 'SELECT_NEW_EMPLOYEES' | 'ADD_NEW_EMPLOYEES' | 'UPDATE_NETPAY';

type UsePayrollOcrParams = {
  isDemo: boolean;
  payrollFlow: ReturnType<typeof usePayrollFlow>;
  currentPayroll: PayrollView;
  companyId: string;
  accounts: AccountView[];
  evidenceLineItems: PayrollEvidenceLineItemView[];
  scrollToTop: () => void;
  onSetPayrollAction: ComponentProps<typeof PayrollFlowModal>['onSetPayrollAction'];
  handleCreateEmployee: ReturnType<typeof useEmployee>['handleCreateEmployee'];
  updatePayrollRecords: ReturnType<typeof usePayrollRecord>['updatePayrollRecords'];
  handleUpdatePayroll: ReturnType<typeof usePayroll>['handleUpdatePayroll'];
  addPayrollRecords: ReturnType<typeof usePayrollRecord>['addPayrollRecords'];
  deletePayrollRecords: ReturnType<typeof usePayrollRecord>['deletePayrollRecords'];
};

export const usePayrollOcr = ({
  isDemo = false,
  payrollFlow,
  currentPayroll,
  companyId,
  accounts,
  evidenceLineItems,
  scrollToTop,
  onSetPayrollAction,
  handleCreateEmployee,
  updatePayrollRecords,
  handleUpdatePayroll,
  addPayrollRecords,
  deletePayrollRecords,
}: UsePayrollOcrParams) => {
  useEffect(() => {
    if (evidenceLineItems.length === 0) return;
    setLineItems(evidenceLineItems);
  }, [evidenceLineItems]);

  const [lineItems, setLineItems] = useState<PayrollEvidenceLineItemView[] | null>(null);

  useEffect(() => {
    if (isDemo) return;
    if (lineItems === null) return;
    if (lineItems.length > 0) {
      parsedEvidence.current = lineItems || [];
      parseEvidence();
    } else {
      setTimeout(() => {
        toast.message('No records found in document');
      }, 500);
      exitFromOCR();
    }
  }, [lineItems]);

  const [step, setStep] = useState<Step>('DOCUMENT_PROCESSING');

  const [isSubmittingNetPayForm, setIsSubmittingNetPayForm] = useState<boolean>(false);

  const [newEmployees, setNewEmployees] = useState<EmployeeView[]>([]);

  const [selectedNewEmployees, setSelectedNewEmployees] = useState<EmployeeView[]>([]);

  const [indexOfNewEmployeesQueue, setIndexOfNewEmployeesQueue] = useState<number>(0);

  const parsedEvidence = useRef<PayrollEvidenceLineItemView[]>([]);

  const currentPayrollRef = useRef<PayrollView>(currentPayroll);

  useEffect(() => {
    currentPayrollRef.current = currentPayroll;
  }, [currentPayroll]);

  const getCurrentPayrollRecords = () => {
    return currentPayrollRef.current.records;
  };

  const parseEvidence = () => {
    const evidenceItems = parsedEvidence.current;
    const newEmployees = getNewEmployeeRecordsFromEvidence(evidenceItems);
    if (newEmployees.length > 0) {
      processNewEmployees();
      return;
    }
    const netPayEmployees = getNetPayRecordsFromEvidence(evidenceItems);
    if (netPayEmployees.length > 0) {
      processEmployeesWithNetPay();
      return;
    }
    setTimeout(() => {
      toast.message(
        netPayEmployees.length > 0 ? 'No net pay records found in document' : 'No new records found in document',
      );
    }, 500);
    exitFromOCR();
  };

  const getNewEmployeeRecordsFromEvidence = (evidenceItems: PayrollEvidenceLineItemView[]) => {
    return evidenceItems.filter((record) => !record.employeeId);
  };

  const getNetPayRecordsFromEvidence = (evidenceItems: PayrollEvidenceLineItemView[]) => {
    return evidenceItems.filter((record) => typeof record.netPay === 'number');
  };

  const processNewEmployees = () => {
    const newEmployees = getNewEmployeeRecordsFromEvidence(parsedEvidence.current);
    setNewEmployees(newEmployees as EmployeeView[]);
    setStep('SELECT_NEW_EMPLOYEES');
  };

  const processEmployeesWithNetPay = async () => {
    await addMissingNetPayEmployeesToPayroll();
    const payrollRecords = getCurrentPayrollRecords();
    if (payrollRecords.length > 0) {
      setStep('UPDATE_NETPAY');
    } else {
      exitFromOCR();
    }
  };

  const addMissingNetPayEmployeesToPayroll = async () => {
    const payrollRecords = getCurrentPayrollRecords();
    const missingEmployeeIdsFromPayroll = getNetPayRecordsFromEvidence(parsedEvidence.current)
      .filter((netPayRecord) => netPayRecord.employeeId)
      .filter(
        (parsedEvidenceRecord) =>
          !payrollRecords.some(
            (payrollRecord: PayrollRecordView) => payrollRecord.employeeId === parsedEvidenceRecord.employeeId,
          ),
      )
      .map((netPayRecord) => netPayRecord.employeeId);
    if (missingEmployeeIdsFromPayroll.length > 0) {
      const addPayrollRecordsPayload = missingEmployeeIdsFromPayroll.map((employeeId) => ({
        employeeId: employeeId as string,
      }));
      const payrollId = payrollFlow?.currentPayroll?.id as string;
      await addPayrollRecords(payrollId, addPayrollRecordsPayload);
    }
    return Promise.resolve();
  };

  const nextEmployee = () => {
    const isQueueEnd = indexOfNewEmployeesQueue === selectedNewEmployees.length - 1;
    if (isQueueEnd) {
      exitFromAddNewEmployeesStep();
    } else {
      scrollToTop();
      setIndexOfNewEmployeesQueue(indexOfNewEmployeesQueue + 1);
    }
  };

  const exitFromAddNewEmployeesStep = () => {
    processEmployeesWithNetPay();
  };

  const exitFromOCR = () => {
    const payrollRecords = getCurrentPayrollRecords();
    if (payrollRecords.length === 0) {
      onSetPayrollAction('MANUAL_ADD_EMPLOYEES');
    } else {
      const payrollStatus = currentPayrollRef.current?.status as PayrollStatus;
      if (payrollStatus === PayrollStatus.WAITING_FOR_APPROVAL) {
        onSetPayrollAction('PAYROLL_SUMMARY');
      } else {
        onSetPayrollAction('FINANCIAL_INFORMATION');
      }
    }
  };

  const handleSaveEmployee = async (formData: EmployeeFormValues) => {
    const address = createAddressObject(formData);
    const destination = createDestinationObject(formData);
    const payload = { ...formData, address, destination, companyId };
    const employee = await handleCreateEmployee(payload);
    parsedEvidence.current = parsedEvidence.current.map((record) => ({
      ...record,
      employeeId:
        record.id === selectedNewEmployees[indexOfNewEmployeesQueue]?.id ? (employee?.id as string) : record.employeeId,
    }));
    const payrollId = payrollFlow?.currentPayroll?.id as string;
    const payrollRecord = [{ employeeId: employee?.id as string }];
    await addPayrollRecords(payrollId, payrollRecord);
    nextEmployee();
  };

  const handleSubmitNetPayForm = async (
    formDataRecords: PayrollFormData['employees'] = [],
    payrollRecords: PayrollRecordView[] = [],
    recordsToDelete: string[] = [],
  ) => {
    setIsSubmittingNetPayForm(true);
    try {
      const firstEligibleModulrAccount = accounts.find(
        (account) =>
          account.fspAdapter === FinancialServiceProviderAdapters.MODULR &&
          account.currency === payrollFlow?.currentPayroll?.currency,
      );
      const sourceAccountConfig =
        firstEligibleModulrAccount && firstEligibleModulrAccount.fspAdapter
          ? {
              isPresent: true as const,
              id: firstEligibleModulrAccount.id,
              fspAdapter: firstEligibleModulrAccount.fspAdapter,
            }
          : {
              isPresent: false as const,
            };
      const payrollCurrency = payrollFlow?.currentPayroll?.currency as Currency;
      const payrollId = payrollFlow?.currentPayroll?.id as string;
      await updatePayrollRecords(payrollId, payrollRecords, formDataRecords, sourceAccountConfig, payrollCurrency);
      await deletePayrollRecords(payrollId, recordsToDelete);
      if (payrollRecords.length > 0) {
        await handleUpdatePayroll(payrollId, {
          status: PayrollStatus.WAITING_FOR_APPROVAL,
        });
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          payrollId: payrollFlow?.currentPayroll?.id,
          payrollReference: payrollFlow?.currentPayroll?.reference,
        },
        tags: {
          component: 'UploadDocumentForm',
          action: 'onSubmitForm(updatePayrollRecords)',
        },
      });
    } finally {
      setIsSubmittingNetPayForm(false);
      exitFromOCR();
    }
  };

  const handleAddSelectedEmployees = (selectedRowIds: string[]) => {
    const selectedEmployees = newEmployees.filter((employee) => selectedRowIds.includes(employee.id));
    setSelectedNewEmployees(selectedEmployees);
    setStep('ADD_NEW_EMPLOYEES');
  };

  const handleSkipNewEmployees = () => {
    exitFromAddNewEmployeesStep();
  };

  const handleSkipEmployee = () => {
    nextEmployee();
  };

  return {
    step,
    newEmployees,
    selectedNewEmployees,
    indexOfNewEmployeesQueue,
    parsedEvidence,
    isSubmittingNetPayForm,
    parseEvidence,
    handleAddSelectedEmployees,
    handleSkipNewEmployees,
    handleSaveEmployee,
    handleSkipEmployee,
    handleSubmitNetPayForm,
  };
};
